import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CircleMarker,
  LayerGroup,
  MapContainer,
  Popup,
  TileLayer,
  useMapEvents,
} from 'react-leaflet';
import { internalAgentApi, getInternalAgentErrorMessage } from '../lib/internalAgentApi';
import { buildRegistryMapPoints } from './delta/connectorLocations';
import './internalDashboards.css';

const REFRESH_MS = 30000;
const REGISTRY_ROW_HEIGHT = 36;
const REGISTRY_OVERSCAN = 8;
const REGISTRY_WINDOW_HEIGHT = 340;

function getSpeechRecognitionCtor() {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function normalizeHealth(value) {
  const txt = String(value || '').toLowerCase();
  if (txt === 'online' || txt === 'live' || txt === 'ok') return 'active';
  if (txt === 'degraded') return 'degraded';
  if (txt === 'offline' || txt === 'error') return 'offline';
  return 'unknown';
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return String(value);
  return d.toISOString().replace('T', ' ').slice(0, 19) + ' UTC';
}

function elapsedLabel(value) {
  if (!value) return 'unknown';
  const at = new Date(value);
  const ms = Date.now() - at.getTime();
  if (Number.isNaN(ms) || ms < 0) return 'unknown';
  const sec = Math.floor(ms / 1000);
  if (sec < 60) return `${sec}s ago`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 48) return `${hr}h ago`;
  return `${Math.floor(hr / 24)}d ago`;
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function summarize(rows, keyGetter, fallback = 'unknown', limit = 8) {
  const counter = new Map();
  rows.forEach((row) => {
    const raw = keyGetter(row);
    const key = raw == null || raw === '' ? fallback : String(raw);
    counter.set(key, (counter.get(key) || 0) + 1);
  });
  return Array.from(counter.entries())
    .map(([label, count]) => ({ label, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

function ZoomTracker({ onZoomChange }) {
  const map = useMapEvents({
    zoomend() {
      onZoomChange(map.getZoom());
    },
  });
  useEffect(() => {
    onZoomChange(map.getZoom());
  }, [map, onZoomChange]);
  return null;
}

function clusterPoints(points, zoom) {
  const bucketSize = zoom <= 2 ? 18 : zoom <= 3 ? 12 : zoom <= 5 ? 6 : zoom <= 7 ? 3 : 1.4;
  const grouped = new Map();
  points.forEach((point) => {
    const latKey = Math.floor(point.lat / bucketSize);
    const lonKey = Math.floor(point.lon / bucketSize);
    const key = `${latKey}:${lonKey}`;
    if (!grouped.has(key)) {
      grouped.set(key, { latTotal: 0, lonTotal: 0, points: [] });
    }
    const group = grouped.get(key);
    group.latTotal += point.lat;
    group.lonTotal += point.lon;
    group.points.push(point);
  });
  return Array.from(grouped.values()).map((group) => ({
    lat: group.latTotal / group.points.length,
    lon: group.lonTotal / group.points.length,
    points: group.points,
    count: group.points.length,
  }));
}

function DeltaClusteredMap({ points, registryPoints, onSelectEvent }) {
  const [zoom, setZoom] = useState(2);
  const clusters = useMemo(() => clusterPoints(points, zoom), [points, zoom]);
  const baseLayer = registryPoints || [];

  return (
    <div className="delta-map-wrap" data-testid="delta-map">
      <MapContainer
        center={[20, -20]}
        zoom={2}
        minZoom={2}
        maxZoom={11}
        scrollWheelZoom
        style={{ width: '100%', height: '360px' }}
      >
        <ZoomTracker onZoomChange={setZoom} />
        <TileLayer
          url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
          attribution="&copy; OpenStreetMap contributors &copy; CARTO"
        />
        {/* Base layer — static connector anchor points (always visible) */}
        <LayerGroup>
          {baseLayer.map((p) => (
            <CircleMarker
              key={`reg:${p.sensor_id}`}
              center={[p.lat, p.lon]}
              radius={4}
              pathOptions={{
                color: p.color,
                weight: 1,
                fillColor: p.color,
                fillOpacity: 0.55,
              }}
            >
              <Popup maxWidth={280}>
                <div className="delta-map-popup" data-testid={`delta-map-registry-${p.sensor_id}`}>
                  <div className="delta-map-popup-title">
                    {p.name || p.sensor_id}
                  </div>
                  <div className="delta-map-popup-list">
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ opacity: 0.7 }}>agent</span>
                      <strong>{p.agent}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ opacity: 0.7 }}>domain</span>
                      <strong>{p.domain || '—'}</strong>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12 }}>
                      <span style={{ opacity: 0.7 }}>health</span>
                      <strong style={{ color: p.color }}>{p.health.toUpperCase()}</strong>
                    </div>
                    <div style={{ marginTop: 6, opacity: 0.6, fontSize: 10 }}>{p.place}</div>
                  </div>
                </div>
              </Popup>
            </CircleMarker>
          ))}
        </LayerGroup>
        {/* Live event clusters (overlay) */}
        <LayerGroup>
          {clusters.map((cluster, idx) => {
            const radius = clamp(6 + Math.log2(cluster.count + 1) * 2.5, 6, 24);
            return (
              <CircleMarker
                key={`${cluster.lat}:${cluster.lon}:${idx}`}
                center={[cluster.lat, cluster.lon]}
                radius={radius}
                pathOptions={{
                  color: '#00e5b0',
                  weight: 1.5,
                  fillColor: '#00e5b0',
                  fillOpacity: 0.24,
                }}
              >
                <Popup maxWidth={320}>
                  <div className="delta-map-popup">
                    <div className="delta-map-popup-title">
                      Cluster · {cluster.count} sensor signals
                    </div>
                    <div className="delta-map-popup-list">
                      {cluster.points.slice(0, 10).map((point) => (
                        <button
                          type="button"
                          key={`${point.event_id}:${point.lat}:${point.lon}`}
                          onClick={() => onSelectEvent(point.event_id)}
                        >
                          <span>{point.signal_type}</span>
                          <span>{point.region || 'global'}</span>
                        </button>
                      ))}
                    </div>
                    {cluster.points.length > 10 && (
                      <div className="delta-map-popup-more">
                        +{cluster.points.length - 10} more rows
                      </div>
                    )}
                  </div>
                </Popup>
              </CircleMarker>
            );
          })}
        </LayerGroup>
      </MapContainer>
    </div>
  );
}

function VirtualizedRegistryTable({ rows, selectedSensorId, onSelectSensor }) {
  const [scrollTop, setScrollTop] = useState(0);
  const totalHeight = rows.length * REGISTRY_ROW_HEIGHT;
  const visibleCount = Math.ceil(REGISTRY_WINDOW_HEIGHT / REGISTRY_ROW_HEIGHT) + REGISTRY_OVERSCAN * 2;
  const start = Math.max(0, Math.floor(scrollTop / REGISTRY_ROW_HEIGHT) - REGISTRY_OVERSCAN);
  const end = Math.min(rows.length, start + visibleCount);
  const visibleRows = rows.slice(start, end);
  const offsetTop = start * REGISTRY_ROW_HEIGHT;

  return (
    <div
      className="delta-registry-virtual-window"
      style={{ height: `${REGISTRY_WINDOW_HEIGHT}px` }}
      onScroll={(event) => setScrollTop(event.currentTarget.scrollTop)}
    >
      <div style={{ height: `${totalHeight}px`, position: 'relative' }}>
        <div style={{ transform: `translateY(${offsetTop}px)` }}>
          {visibleRows.map((row) => {
            const health = normalizeHealth(row.health);
            return (
              <button
                type="button"
                key={row.sensor_id}
                className={`delta-registry-row${selectedSensorId === row.sensor_id ? ' selected' : ''}`}
                onClick={() => onSelectSensor(row.sensor_id)}
              >
                <span>{row.name}</span>
                <span>{row.agent}</span>
                <span>{row.source_class || 'connector'}</span>
                <span className={`delta-health-pill ${health}`}>{health}</span>
                <span>{elapsedLabel(row.last_fetch_time)}</span>
                <span>{row.source_mode || 'primary_bus'}</span>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

export default function InternalDeltaDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registry, setRegistry] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [failures, setFailures] = useState(null);
  const [busSummary, setBusSummary] = useState(null);
  const [busEvents, setBusEvents] = useState([]);
  const [geojson, setGeojson] = useState({ type: 'FeatureCollection', features: [] });
  const [feedComparison, setFeedComparison] = useState({ summary: {}, sensors: [] });
  const [stenographer, setStenographer] = useState({ normalized_turns: [] });
  const [sensorHealth, setSensorHealth] = useState(null);
  const [selectedSensorId, setSelectedSensorId] = useState('');
  const [selectedSensorDetail, setSelectedSensorDetail] = useState(null);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [selectedEventDetail, setSelectedEventDetail] = useState(null);
  const [registrySearch, setRegistrySearch] = useState('');
  const [registryHealthFilter, setRegistryHealthFilter] = useState('all');
  const [showRegistryDrilldown, setShowRegistryDrilldown] = useState(false);
  const [saveMode, setSaveMode] = useState('operator_note');
  const [noteText, setNoteText] = useState('');
  const [tagRegion, setTagRegion] = useState('');
  const [tagSensor, setTagSensor] = useState('');
  const [tagEvent, setTagEvent] = useState('');
  const [noteLog, setNoteLog] = useState([]);
  const [noteSubmitting, setNoteSubmitting] = useState(false);
  const [voiceReady, setVoiceReady] = useState(false);
  const [voiceRecording, setVoiceRecording] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const recognitionRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [
        registryRes,
        pipelineRes,
        failuresRes,
        busSummaryRes,
        busEventsRes,
        feedComparisonRes,
        stenographerRes,
        sensorHealthRes,
      ] = await Promise.all([
        internalAgentApi.get('/api/internal/sensors/registry'),
        internalAgentApi.get('/api/internal/sensors/pipeline'),
        internalAgentApi.get('/api/internal/sensors/failures'),
        internalAgentApi.get('/api/internal/bus/summary'),
        internalAgentApi.get('/api/internal/bus/events', {
          params: { limit: 500, time_window_minutes: 360 },
        }),
        internalAgentApi.get('/api/internal/bus/feed-comparison', {
          params: { window_minutes: 360 },
        }),
        internalAgentApi.get('/api/internal/ops/stenographer', {
          params: { limit: 200 },
        }),
        internalAgentApi.get('/api/internal/sensors/health'),
      ]);
      const connectorRows = registryRes.data?.connectors || [];
      const eventRows = busEventsRes.data?.events || [];
      setRegistry(connectorRows);
      setPipeline(pipelineRes.data?.pipeline || []);
      setFailures(failuresRes.data || null);
      setBusSummary(busSummaryRes.data || null);
      setBusEvents(eventRows);
      setGeojson(busEventsRes.data?.geojson || { type: 'FeatureCollection', features: [] });
      setFeedComparison(feedComparisonRes.data || { summary: {}, sensors: [] });
      setStenographer(stenographerRes.data || { normalized_turns: [] });
      setSensorHealth(sensorHealthRes.data || null);
      if (!selectedSensorId && connectorRows[0]?.sensor_id) {
        setSelectedSensorId(connectorRows[0].sensor_id);
      }
      if (!selectedEventId && eventRows[0]?.event_id) {
        setSelectedEventId(eventRows[0].event_id);
      }
      setError('');
    } catch (err) {
      setError(getInternalAgentErrorMessage(err, 'Failed to load Delta system state.'));
    } finally {
      setLoading(false);
    }
  }, [selectedEventId, selectedSensorId]);

  useEffect(() => {
    load();
    const timer = setInterval(load, REFRESH_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    let active = true;
    if (!selectedSensorId) {
      setSelectedSensorDetail(null);
      return () => {
        active = false;
      };
    }
    internalAgentApi
      .get(`/api/internal/sensors/detail/${encodeURIComponent(selectedSensorId)}`)
      .then((res) => {
        if (active) setSelectedSensorDetail(res.data || null);
      })
      .catch(() => {
        if (active) setSelectedSensorDetail(null);
      });
    return () => {
      active = false;
    };
  }, [selectedSensorId]);

  useEffect(() => {
    let active = true;
    if (!selectedEventId) {
      setSelectedEventDetail(null);
      return () => {
        active = false;
      };
    }
    internalAgentApi
      .get(`/api/internal/bus/events/${encodeURIComponent(selectedEventId)}`)
      .then((res) => {
        if (active) setSelectedEventDetail(res.data || null);
      })
      .catch(() => {
        if (active) setSelectedEventDetail(null);
      });
    return () => {
      active = false;
    };
  }, [selectedEventId]);

  useEffect(() => {
    const SpeechRecognition = getSpeechRecognitionCtor();
    setVoiceReady(Boolean(SpeechRecognition));
  }, []);

  const sensorStats = useMemo(() => {
    const counts = { total: registry.length, active: 0, offline: 0, degraded: 0, unknown: 0 };
    let cameras = 0;
    registry.forEach((row) => {
      const normalized = normalizeHealth(row.health);
      counts[normalized] = (counts[normalized] || 0) + 1;
      const descriptor = `${row.source_class || ''} ${row.name || ''}`.toLowerCase();
      if (descriptor.includes('camera')) cameras += 1;
    });
    return {
      ...counts,
      cameras,
      nonCamera: Math.max(0, registry.length - cameras),
    };
  }, [registry]);

  const mapPoints = useMemo(() => {
    const points = [];
    (geojson.features || []).forEach((feature) => {
      if (!feature || feature.geometry?.type !== 'Point') return;
      const coords = feature.geometry.coordinates || [];
      if (!Array.isArray(coords) || coords.length < 2) return;
      const lon = Number(coords[0]);
      const lat = Number(coords[1]);
      if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
      points.push({
        event_id: feature.properties?.event_id || '',
        signal_type: feature.properties?.signal_type || 'signal',
        region: feature.properties?.region_location || '',
        lat,
        lon,
      });
    });
    return points;
  }, [geojson]);

  const registryMapPoints = useMemo(() => buildRegistryMapPoints(registry), [registry]);

  const groupedType = useMemo(
    () => summarize(registry, (row) => row.source_class || row.source_type, 'unknown'),
    [registry]
  );
  const groupedSource = useMemo(
    () => summarize(registry, (row) => row.name, 'unknown'),
    [registry]
  );
  const groupedAgent = useMemo(() => summarize(registry, (row) => row.agent, 'unknown'), [registry]);
  const groupedConnector = useMemo(
    () => summarize(registry, (row) => `${row.agent}:${row.name}`, 'unknown'),
    [registry]
  );
  const groupedRegion = useMemo(
    () => summarize(busEvents, (row) => row.region_location, 'unmapped'),
    [busEvents]
  );

  const filteredRegistry = useMemo(() => {
    const q = registrySearch.trim().toLowerCase();
    return registry.filter((row) => {
      const health = normalizeHealth(row.health);
      if (registryHealthFilter !== 'all' && health !== registryHealthFilter) return false;
      if (!q) return true;
      const haystack = [
        row.name,
        row.agent,
        row.source_class,
        row.domain,
        row.sensor_id,
        row.source_mode,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [registry, registrySearch, registryHealthFilter]);

  const feedRows = feedComparison.sensors || [];
  const feedSummary = feedComparison.summary || {};

  const pipelineHeartbeat = useMemo(() => {
    const stageCounter = new Map();
    pipeline.forEach((row) => {
      (row.pipeline || []).forEach((stage) => {
        const key = `${stage.stage}:${stage.status}`;
        stageCounter.set(key, (stageCounter.get(key) || 0) + 1);
      });
    });
    return Array.from(stageCounter.entries())
      .map(([stage, count]) => ({ stage, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 6);
  }, [pipeline]);

  const stateClerk = useMemo(() => {
    const staleSensors = failures?.stale_sensors?.length || 0;
    const failingSensors = failures?.failing_connectors?.length || 0;
    const unresolvedPredictions = stenographer?.open_predictions || 0;
    const disputes = busSummary?.bus_health?.disputed_event_count || 0;
    const changed = [
      `${feedSummary.fallback_active || 0} connectors are in fallback mode.`,
      `${staleSensors} sensors are stale and ${failingSensors} are failing.`,
      `${disputes} disputed signals remain in the bus window.`,
    ];
    const why = [
      'Fallback-active connectors reduce provenance confidence and increase lag risk.',
      'Stale or failing sensors create regional coverage blind spots.',
      'Disputed signals increase operator review load before downstream use.',
    ];
    const watch = [
      `${feedSummary.lagging || 0} lagging feeds that can diverge next.`,
      `${unresolvedPredictions} open transcript predictions needing closure.`,
      `${sensorStats.degraded} degraded sensors likely to move offline.`,
    ];
    return { changed, why, watch };
  }, [busSummary, failures, feedSummary, sensorStats.degraded, stenographer]);

  const digestRows = useMemo(() => {
    const events = busEvents.slice(0, 14).map((row) => ({
      id: `evt:${row.event_id}`,
      stamp: row.timestamp,
      text: `${row.signal_type} · ${row.status} · ${row.region_location || 'global'}`,
      source: 'event',
    }));
    const transcriptRows = (stenographer?.normalized_turns || []).slice(0, 14).map((row, idx) => ({
      id: `tx:${idx}`,
      stamp: row.detected_at || '',
      text: `${row.speaker_label || 'operator'}: ${row.utterance_text || row.text || ''}`,
      source: 'transcript',
    }));
    return [...events, ...transcriptRows]
      .filter((row) => row.text)
      .sort((a, b) => new Date(b.stamp).getTime() - new Date(a.stamp).getTime())
      .slice(0, 20);
  }, [busEvents, stenographer]);

  const beginVoiceCapture = useCallback(() => {
    const SpeechRecognition = getSpeechRecognitionCtor();
    if (!SpeechRecognition) {
      setVoiceError('Voice capture is unavailable in this browser.');
      return;
    }
    setVoiceError('');
    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognition.onresult = (event) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        transcript += event.results[i][0].transcript;
      }
      if (transcript) {
        setNoteText((prev) => `${prev}${prev ? ' ' : ''}${transcript.trim()}`);
      }
    };
    recognition.onerror = (event) => {
      setVoiceError(`Voice capture error: ${event.error}`);
      setVoiceRecording(false);
    };
    recognition.onend = () => {
      setVoiceRecording(false);
    };
    recognitionRef.current = recognition;
    recognition.start();
    setVoiceRecording(true);
  }, []);

  const stopVoiceCapture = useCallback(() => {
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    setVoiceRecording(false);
  }, []);

  const submitNote = useCallback(
    async (event) => {
      event.preventDefault();
      const text = noteText.trim();
      if (!text) return;
      const tags = {
        region: tagRegion.trim(),
        sensor: tagSensor.trim(),
        event: tagEvent.trim(),
      };
      const tagTokens = Object.entries(tags)
        .filter(([, value]) => value)
        .map(([key, value]) => `${key}:${value}`);
      const taggedText = tagTokens.length ? `${text} [${tagTokens.join(' | ')}]` : text;
      setNoteSubmitting(true);
      try {
        if (saveMode === 'transcript_event') {
          const res = await internalAgentApi.post('/api/speech/transcript', {
            utterance_text: taggedText,
            confidence: 0.9,
            speaker_label: 'Operator',
            source_type: 'operator_command',
            is_final: true,
          });
          setNoteLog((prev) => [
            {
              id: `note-${Date.now()}`,
              mode: 'transcript_event',
              at: new Date().toISOString(),
              text: taggedText,
              responseId: res.data?.events?.[0]?.event_id || '',
            },
            ...prev,
          ]);
        } else {
          setNoteLog((prev) => [
            {
              id: `note-${Date.now()}`,
              mode: 'operator_note',
              at: new Date().toISOString(),
              text: taggedText,
              responseId: '',
            },
            ...prev,
          ]);
        }
        setNoteText('');
        setTagRegion('');
        setTagSensor('');
        setTagEvent('');
      } catch (err) {
        setVoiceError(getInternalAgentErrorMessage(err, 'Failed to store operator note.'));
      } finally {
        setNoteSubmitting(false);
      }
    },
    [noteText, saveMode, tagEvent, tagRegion, tagSensor]
  );

  if (loading) {
    return (
      <div className="nova-loading" data-testid="internal-delta-loading">
        <div className="loading-ring" />
        <div className="loading-text">Delta</div>
        <div className="loading-sub">Loading global sensor registry and state bus...</div>
      </div>
    );
  }

  return (
    <div className="nova-app internal-agent-shell" data-testid="internal-delta-dashboard">
      <div className="topbar internal-agent-topbar">
        <div className="topbar-dot" />
        <span className="topbar-logo">Delta</span>
        <div className="topbar-sep" />
        <span className="topbar-sub">GLOBAL SENSOR CONTROL · STATE DIGEST</span>
        <div className="topbar-spacer" />
        <span className="topbar-regions">{sensorStats.total} TOTAL SENSORS</span>
        <span className="topbar-sensors">{sensorStats.active} ACTIVE</span>
        <span className="topbar-degraded">{sensorStats.degraded} DEGRADED</span>
        <button className="topbar-refresh" onClick={load} type="button">
          REFRESH
        </button>
      </div>
      <div className="nova-body internal-agent-body">
        <aside className="internal-agent-column">
          <section className="panel">
            <h3>Global Sensor Status</h3>
            <div className="internal-grid kpi-grid">
              <div className="kpi-card"><span>Total Sensors</span><strong>{sensorStats.total}</strong></div>
              <div className="kpi-card"><span>Active Sensors</span><strong>{sensorStats.active}</strong></div>
              <div className="kpi-card"><span>Offline Sensors</span><strong>{sensorStats.offline}</strong></div>
              <div className="kpi-card"><span>Degraded Sensors</span><strong>{sensorStats.degraded}</strong></div>
              <div className="kpi-card"><span>Total Cameras</span><strong>{sensorStats.cameras}</strong></div>
              <div className="kpi-card"><span>Non-camera Sensors</span><strong>{sensorStats.nonCamera}</strong></div>
            </div>
          </section>

          <section className="panel">
            <h3>Grouped Summaries</h3>
            <div className="internal-grid two-col">
              <div className="delta-summary-block">
                <div className="delta-summary-label">By sensor type</div>
                <div className="delta-summary-list">
                  {groupedType.map((row) => (
                    <div key={row.label}><span>{row.label}</span><strong>{row.count}</strong></div>
                  ))}
                </div>
              </div>
              <div className="delta-summary-block">
                <div className="delta-summary-label">By source</div>
                <div className="delta-summary-list">
                  {groupedSource.map((row) => (
                    <div key={row.label}><span>{row.label}</span><strong>{row.count}</strong></div>
                  ))}
                </div>
              </div>
              <div className="delta-summary-block">
                <div className="delta-summary-label">By region</div>
                <div className="delta-summary-list">
                  {groupedRegion.map((row) => (
                    <div key={row.label}><span>{row.label}</span><strong>{row.count}</strong></div>
                  ))}
                </div>
              </div>
              <div className="delta-summary-block">
                <div className="delta-summary-label">By agent</div>
                <div className="delta-summary-list">
                  {groupedAgent.map((row) => (
                    <div key={row.label}><span>{row.label}</span><strong>{row.count}</strong></div>
                  ))}
                </div>
              </div>
              <div className="delta-summary-block delta-summary-full">
                <div className="delta-summary-label">By connector</div>
                <div className="delta-summary-list">
                  {groupedConnector.map((row) => (
                    <div key={row.label}><span>{row.label}</span><strong>{row.count}</strong></div>
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className="panel">
            <h3>Heartbeat + Freshness</h3>
            <div className="meta-list">
              <div><span>Registry updated</span><strong>{formatDate(sensorHealth?.updated_at)}</strong></div>
              <div><span>Fetched signals</span><strong>{sensorHealth?.fetched_signal_count ?? 0}</strong></div>
              <div><span>Published events</span><strong>{sensorHealth?.published_event_count ?? 0}</strong></div>
              <div><span>Coverage gaps (partial)</span><strong>{Object.keys(failures?.partial_coverage || {}).length}</strong></div>
              <div><span>Stale sensors</span><strong>{failures?.stale_sensors?.length || 0}</strong></div>
              <div><span>Quarantined outputs</span><strong>{failures?.quarantine_outputs || 0}</strong></div>
            </div>
          </section>
        </aside>

        <main className="internal-agent-column internal-agent-column-wide">
          {error && <div className="internal-error">{error}</div>}
          <section className="panel">
            <h3>Clustered World Sensor Locations</h3>
            <div className="internal-note">
              Clustered view renders map points from available geotagged sensor publications.
            </div>
            <DeltaClusteredMap points={mapPoints} registryPoints={registryMapPoints} onSelectEvent={setSelectedEventId} />
          </section>

          <section className="panel">
            <h3>Feed Health · Primary vs Fallback</h3>
            <div className="internal-grid kpi-grid">
              <div className="kpi-card"><span>In sync</span><strong>{feedSummary.in_sync || 0}</strong></div>
              <div className="kpi-card"><span>Lagging</span><strong>{feedSummary.lagging || 0}</strong></div>
              <div className="kpi-card"><span>Diverged</span><strong>{feedSummary.diverged || 0}</strong></div>
              <div className="kpi-card"><span>Fallback active</span><strong>{feedSummary.fallback_active || 0}</strong></div>
              <div className="kpi-card"><span>No signal</span><strong>{feedSummary.no_signal || 0}</strong></div>
              <div className="kpi-card"><span>Compared connectors</span><strong>{feedSummary.total || 0}</strong></div>
            </div>
            <div className="delta-feed-table">
              <div className="delta-feed-row delta-feed-head">
                <span>Connector</span>
                <span>Primary</span>
                <span>Fallback</span>
                <span>Status</span>
                <span>Last publish</span>
                <span>Last successful fetch</span>
              </div>
              {feedRows.slice(0, 24).map((row) => (
                <div className="delta-feed-row" key={`${row.agent}:${row.connector}`}>
                  <span>{row.agent}/{row.connector}</span>
                  <span>{row.feed_status?.primary_bus?.status || 'unknown'}</span>
                  <span>{row.feed_status?.agent_bridge?.status || 'unknown'}</span>
                  <span>{row.diff_status || 'unknown'}</span>
                  <span>{elapsedLabel(row.feed_status?.primary_bus?.last_update)}</span>
                  <span>{elapsedLabel(row.feed_status?.agent_bridge?.last_update)}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <div className="internal-header">
              <div>
                <h3>Registry Drilldown (Search + Virtualized)</h3>
                <p>Heavy registry rows are loaded only on drilldown to keep default Delta lightweight.</p>
              </div>
              <button
                className="internal-btn"
                type="button"
                onClick={() => setShowRegistryDrilldown((prev) => !prev)}
              >
                {showRegistryDrilldown ? 'HIDE DRILLDOWN' : 'OPEN DRILLDOWN'}
              </button>
            </div>
            {showRegistryDrilldown && (
              <>
                <div className="filter-grid">
                  <input
                    placeholder="Search sensor, connector, agent, source..."
                    value={registrySearch}
                    onChange={(event) => setRegistrySearch(event.target.value)}
                  />
                  <select
                    value={registryHealthFilter}
                    onChange={(event) => setRegistryHealthFilter(event.target.value)}
                  >
                    <option value="all">all health</option>
                    <option value="active">active</option>
                    <option value="degraded">degraded</option>
                    <option value="offline">offline</option>
                    <option value="unknown">unknown</option>
                  </select>
                </div>
                <VirtualizedRegistryTable
                  rows={filteredRegistry}
                  selectedSensorId={selectedSensorId}
                  onSelectSensor={setSelectedSensorId}
                />
              </>
            )}
          </section>
        </main>

        <aside className="internal-agent-column">
          <section className="panel">
            <h3>State Clerk</h3>
            <div className="delta-clerk-grid">
              <div>
                <h4>Rolling Summaries</h4>
                <ul>
                  <li>Throughput/min: {busSummary?.bus_health?.event_throughput_per_minute ?? 0}</li>
                  <li>Active events: {busSummary?.bus_health?.active_event_count ?? 0}</li>
                  <li>Disputed events: {busSummary?.bus_health?.disputed_event_count ?? 0}</li>
                </ul>
              </div>
              <div>
                <h4>What Changed</h4>
                <ul>{stateClerk.changed.map((line) => <li key={line}>{line}</li>)}</ul>
              </div>
              <div>
                <h4>Why It Matters</h4>
                <ul>{stateClerk.why.map((line) => <li key={line}>{line}</li>)}</ul>
              </div>
              <div>
                <h4>What to Watch Next</h4>
                <ul>{stateClerk.watch.map((line) => <li key={line}>{line}</li>)}</ul>
              </div>
            </div>
          </section>

          <section className="panel">
            <h3>Event + Transcript Digest</h3>
            <div className="delta-digest-list">
              {digestRows.map((row) => (
                <div key={row.id}>
                  <span>{row.source.toUpperCase()}</span>
                  <strong>{row.text}</strong>
                  <small>{formatDate(row.stamp)}</small>
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <h3>Operator Notes + Voice Capture</h3>
            <form onSubmit={submitNote} className="delta-note-form">
              <textarea
                placeholder="Capture operator observation, spoken transcript, or incident context..."
                value={noteText}
                onChange={(event) => setNoteText(event.target.value)}
              />
              <div className="filter-grid">
                <input
                  placeholder="tag region (optional)"
                  value={tagRegion}
                  onChange={(event) => setTagRegion(event.target.value)}
                />
                <input
                  placeholder="tag sensor (optional)"
                  value={tagSensor}
                  onChange={(event) => setTagSensor(event.target.value)}
                />
                <input
                  placeholder="tag event (optional)"
                  value={tagEvent}
                  onChange={(event) => setTagEvent(event.target.value)}
                />
                <select value={saveMode} onChange={(event) => setSaveMode(event.target.value)}>
                  <option value="operator_note">store as operator note</option>
                  <option value="transcript_event">store as transcript event</option>
                </select>
              </div>
              <div className="delta-note-actions">
                <button className="internal-btn" type="submit" disabled={noteSubmitting}>
                  {noteSubmitting ? 'SAVING...' : 'SAVE NOTE'}
                </button>
                {voiceReady && !voiceRecording && (
                  <button className="internal-btn" type="button" onClick={beginVoiceCapture}>
                    START VOICE
                  </button>
                )}
                {voiceRecording && (
                  <button className="internal-btn" type="button" onClick={stopVoiceCapture}>
                    STOP VOICE
                  </button>
                )}
              </div>
            </form>
            {voiceError && <div className="internal-error">{voiceError}</div>}
            <div className="delta-digest-list">
              {noteLog.slice(0, 8).map((item) => (
                <div key={item.id}>
                  <span>{item.mode === 'transcript_event' ? 'TRANSCRIPT' : 'NOTE'}</span>
                  <strong>{item.text}</strong>
                  <small>{formatDate(item.at)}</small>
                </div>
              ))}
            </div>
          </section>

          <section className="panel">
            <h3>Provenance + Sensor Detail</h3>
            <div className="meta-list">
              <div><span>Selected sensor</span><strong>{selectedSensorDetail?.name || '—'}</strong></div>
              <div><span>Last fetch</span><strong>{formatDate(selectedSensorDetail?.last_fetch_time)}</strong></div>
              <div><span>Source mode</span><strong>{selectedSensorDetail?.source_mode || '—'}</strong></div>
              <div><span>Bridge reason</span><strong>{selectedSensorDetail?.bridge_reason || '—'}</strong></div>
              <div><span>Selected event</span><strong>{selectedEventDetail?.event_id || selectedEventId || '—'}</strong></div>
              <div><span>Primary authority</span><strong>{selectedEventDetail?.authority_view?.primary_authority || '—'}</strong></div>
            </div>
            <pre className="internal-json">
              {JSON.stringify(
                {
                  pipeline_heartbeat: pipelineHeartbeat,
                  event_provenance: selectedEventDetail?.evidence_bundle || [],
                },
                null,
                2
              )}
            </pre>
          </section>
        </aside>
      </div>
    </div>
  );
}
